import React from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Plus, Trash2, UserPlus, Users, Shield, CheckCircle, XCircle, Edit, Eye, EyeOff 
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import api, { getAuthUser } from '@/lib/api';

export default function AdminUsuarios() {
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showModal, setShowModal] = React.useState(false);
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [editingUser, setEditingUser] = React.useState(null);
  const [showPassword, setShowPassword] = React.useState(false);
  const currentUser = getAuthUser();
  
  const [newUser, setNewUser] = React.useState({
    nome: '',
    email: '',
    senha: '',
    cargo: ''
  });

  const [editForm, setEditForm] = React.useState({
    nome: '',
    email: '',
    cargo: '',
    senha: '',
    is_active: true,
    is_master: false
  });

  React.useEffect(() => {
    if (!currentUser?.is_master) {
      toast.error('Acesso restrito ao usuário mestre');
      return;
    }
    fetchUsers();
  }, [currentUser]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.nome || !newUser.email || !newUser.senha) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      await api.post('/users', newUser);
      toast.success('Usuário criado com sucesso!');
      setShowModal(false);
      setNewUser({ nome: '', email: '', senha: '', cargo: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      nome: user.nome,
      email: user.email,
      cargo: user.cargo || '',
      senha: '',
      is_active: user.is_active,
      is_master: user.is_master
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editForm.nome || !editForm.email) {
      toast.error('Nome e email são obrigatórios');
      return;
    }

    try {
      const updateData = {
        nome: editForm.nome,
        email: editForm.email,
        cargo: editForm.cargo,
        is_active: editForm.is_active,
        is_master: editForm.is_master
      };

      if (editForm.senha) {
        updateData.senha = editForm.senha;
      }

      await api.put(`/users/${editingUser.user_id}`, updateData);
      toast.success('Usuário atualizado com sucesso!');
      setShowEditModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar usuário');
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      const response = await api.put(`/users/${userId}/toggle-status`);
      toast.success(response.data.message);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar status');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Excluir o usuário "${userName}"?`)) return;

    try {
      await api.delete(`/users/${userId}`);
      toast.success('Usuário excluído!');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  if (!currentUser?.is_master) {
    return (
      <AdminLayout>
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="mx-auto mb-4 text-red-500" size={48} />
            <h2 className="font-heading text-xl font-bold text-gray-900 mb-2">Acesso Restrito</h2>
            <p className="text-gray-600">Apenas o usuário mestre pode gerenciar usuários.</p>
          </CardContent>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-usuarios">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="font-heading text-3xl font-bold text-gray-900">Gerenciar Usuários</h1>
            <p className="text-gray-600">{users.length} usuário(s) cadastrado(s)</p>
          </div>
          
          <Button 
            onClick={() => setShowModal(true)} 
            className="bg-[#B91C1C] hover:bg-[#991B1B]"
            data-testid="new-user-btn"
          >
            <UserPlus size={18} className="mr-2" />
            Novo Usuário
          </Button>
        </div>

        {/* Users List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : users.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <Users className="mx-auto mb-4 text-gray-300" size={48} />
              <p>Nenhum usuário cadastrado</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Nome</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">E-mail</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Cargo</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Tipo</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Cadastro</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.user_id} className="border-b hover:bg-gray-50" data-testid={`user-row-${user.user_id}`}>
                        <td className="py-3 px-4 font-medium">{user.nome}</td>
                        <td className="py-3 px-4 text-gray-600">{user.email}</td>
                        <td className="py-3 px-4 text-gray-600">{user.cargo || '-'}</td>
                        <td className="py-3 px-4">
                          {user.is_active ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle size={12} className="mr-1" />
                              Ativo
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">
                              <XCircle size={12} className="mr-1" />
                              Inativo
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {user.is_master ? (
                            <Badge className="bg-purple-100 text-purple-800">
                              <Shield size={12} className="mr-1" />
                              Master
                            </Badge>
                          ) : (
                            <Badge variant="outline">Comum</Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-600">{formatDate(user.created_at)}</td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditUser(user)}
                              title="Editar usuário"
                            >
                              <Edit size={14} />
                            </Button>
                            {!user.is_master && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleToggleStatus(user.user_id)}
                                >
                                  {user.is_active ? 'Desativar' : 'Ativar'}
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDeleteUser(user.user_id, user.nome)}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create User Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Novo Usuário</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <Label>Nome Completo *</Label>
                <Input
                  value={newUser.nome}
                  onChange={(e) => setNewUser({ ...newUser, nome: e.target.value })}
                  placeholder="Nome do usuário"
                  className="mt-1"
                  data-testid="user-name-input"
                />
              </div>

              <div>
                <Label>E-mail *</Label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="email@exemplo.com"
                  className="mt-1"
                  data-testid="user-email-input"
                />
              </div>

              <div>
                <Label>Senha *</Label>
                <Input
                  type="password"
                  value={newUser.senha}
                  onChange={(e) => setNewUser({ ...newUser, senha: e.target.value })}
                  placeholder="Senha de acesso"
                  className="mt-1"
                  data-testid="user-password-input"
                />
              </div>

              <div>
                <Label>Cargo</Label>
                <Input
                  value={newUser.cargo}
                  onChange={(e) => setNewUser({ ...newUser, cargo: e.target.value })}
                  placeholder="Ex: Secretário de Administração"
                  className="mt-1"
                  data-testid="user-cargo-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#B91C1C] hover:bg-[#991B1B]" data-testid="create-user-btn">
                  Criar Usuário
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit User Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">Editar Usuário</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <Label>Nome Completo *</Label>
                <Input
                  value={editForm.nome}
                  onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
                  placeholder="Nome do usuário"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>E-mail *</Label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="email@exemplo.com"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Cargo</Label>
                <Input
                  value={editForm.cargo}
                  onChange={(e) => setEditForm({ ...editForm, cargo: e.target.value })}
                  placeholder="Ex: Secretário de Administração"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Nova Senha (deixe em branco para manter a atual)</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={editForm.senha}
                    onChange={(e) => setEditForm({ ...editForm, senha: e.target.value })}
                    placeholder="Nova senha"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </Button>
                </div>
              </div>

              {/* Privilégios */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold text-gray-900 mb-3">Privilégios</h4>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-medium">Usuário Ativo</Label>
                      <p className="text-sm text-gray-500">Permite acesso ao sistema</p>
                    </div>
                    <Switch
                      checked={editForm.is_active}
                      onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
                      disabled={editingUser?.is_master}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-medium">Privilégio Master</Label>
                      <p className="text-sm text-gray-500">Acesso total ao sistema (gerenciar usuários, configurações)</p>
                    </div>
                    <Switch
                      checked={editForm.is_master}
                      onCheckedChange={(checked) => setEditForm({ ...editForm, is_master: checked })}
                      disabled={editingUser?.user_id === currentUser?.user_id && editingUser?.is_master}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#B91C1C] hover:bg-[#991B1B]">
                  Salvar Alterações
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
